import * as React from 'react';
import { FieldError, UseFormRegisterReturn } from 'react-hook-form';

import {
  Theme,
  SxProps,
  InputLabel,
  InputBaseComponentProps,
  TextField as TextFieldUI,
  TextFieldProps as TextFieldUIProps,
} from '@mui/material';

import { Asterisk } from '@/components/Asterisk';

import { stylesTextField } from './TextField.styles';

type TextFieldProps = {
  id?: string;
  type?: string;
  minRows?: number;
  sx?: SxProps<Theme>;
  readOnly?: boolean;
  error?: FieldError;
  disabled?: boolean;
  multiline?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  isRequired?: boolean;
  label?: string | null;
  labelSx?: SxProps<Theme>;
  size?: 'small' | 'medium';
  endAdornment?: JSX.Element;
  inputProps?: InputBaseComponentProps;
  value?: string | number | null;
  variant?: TextFieldUIProps['variant'];
  defaultValue?: string | number | null;
  registration?: Partial<UseFormRegisterReturn>;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const TextField: React.FunctionComponent<TextFieldProps> = (props) => {
  const {
    onKeyDown,
    onChange,
    onPaste,
    onBlur,
    id,
    sx,
    type,
    size,
    label,
    value,
    error,
    minRows,
    labelSx,
    readOnly,
    disabled,
    multiline,
    isRequired,
    inputProps,
    placeholder,
    defaultValue,
    registration,
    endAdornment,
    fullWidth = true,
    variant = 'outlined',
  } = props;
  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);

    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };
  return (
    <>
      <InputLabel htmlFor={id} sx={labelSx}>
        {label}
        {isRequired ? <Asterisk /> : ''}
      </InputLabel>
      <TextFieldUI
        value={value}
        {...registration}
        id={id}
        type={type}
        minRows={minRows}
        variant={variant}
        fullWidth={fullWidth}
        defaultValue={defaultValue}
        InputLabelProps={{ shrink: true }}
        onKeyDown={handleOnKeyDown}
        onPaste={onPaste}
        InputProps={{
          endAdornment,
          inputProps: {
            ...inputProps,
            onChange,
            onBlur,
            'data-testid': `${id}`,
          },
          readOnly,
          disabled,
        }}
        error={!!error}
        helperText={error?.message}
        size={size}
        multiline={multiline}
        sx={{ ...stylesTextField, ...sx }}
        placeholder={placeholder}
      />
    </>
  );
};
